/**
 * Sales Brokers & Representatives — 8 documents.
 *
 * A broker represents you and never takes title. The house-account definition and the
 * post-termination commission tail are where founders lose the most money.
 */
export const BROKERS_DOCS = [
  {
    id: "cpg-broker-agreement",
    name: "CPG Broker Agreement",
    category: "brokers",
    subcategory: "Core broker agreements",
    aliases: [
      "Food Broker Agreement",
      "Sales Broker Agreement",
      "Brokerage Agreement",
      "Manufacturer Representative Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Sales Broker",
    whenYouNeedIt:
      "When you engage a broker to open retail or foodservice accounts you cannot reach yourself. Sign it before the broker starts calling on buyers, because the accounts they touch in week one are the ones you will argue about later.",
    whatItDoes:
      "Appoints a broker to represent your brand to buyers in a defined set of accounts, channels or territories, in exchange for commission on the sales it produces. The broker never buys your product and never takes title — you invoice the customer and you carry the receivable.",
    whyItMatters:
      "Two clauses decide whether this agreement is cheap or expensive: the definition of a house account, and the post-termination commission tail. Get house accounts wrong and you pay commission on business you brought in yourself, sometimes for the life of the relationship. Get the tail wrong and you keep paying a broker who has stopped working, on accounts that now reorder automatically. Founders also confuse brokers with distributors: a distributor buys your product and resells it on its own margin, a broker never takes title at all. Signing the wrong one means giving away pricing control and the customer relationship when you thought you were hiring a salesperson.",
    majorTerms: [
      "Commission percentage and the basis it is calculated on",
      "When commission is earned and when it is paid",
      "Commission on repeat and reorder volume",
      "House account definition and list",
      "Existing accounts and how they are treated",
      "New account definition and qualification",
      "Territory, channel and account scope",
      "Whether the appointment is exclusive",
      "Performance requirements and account targets",
      "Treatment of retailer chargebacks, deductions and returns",
      "Commission treatment of promotional and discounted volume",
      "Broker expenses and what is reimbursable",
      "Term, termination and notice period",
      "Post-termination commission tail",
    ],
    clausesToWatch: [
      {
        clause: "House account definition",
        why: "If house accounts are not named in a schedule, a broker can claim commission on accounts you opened and service yourself, and the argument is unwinnable once the invoice exists.",
      },
      {
        clause: "Commission earned versus paid",
        why: "Commission earned on order rather than on payment means you owe the broker even on orders the retailer never pays for.",
      },
      {
        clause: "Post-termination commission tail",
        why: "An open-ended tail means you pay on reorders forever, on accounts that now run themselves, long after the broker has stopped calling on anyone.",
      },
      {
        clause: "Commission basis",
        why: "Commission on gross invoice rather than net of deductions and allowances means you pay a percentage on money you never collected.",
      },
      {
        clause: "Chargeback, deduction and return treatment",
        why: "If commission is not reversed when a sale is deducted, discounted or returned, the broker keeps a percentage of revenue that no longer exists.",
      },
      {
        clause: "Exclusivity of appointment",
        why: "An exclusive broker across a whole channel with no performance requirement blocks you from hiring anyone else while producing nothing.",
      },
      {
        clause: "Performance requirements",
        why: "Without account or revenue targets tied to termination, an underperforming broker has no consequence and no urgency.",
      },
      {
        clause: "Expense reimbursement",
        why: "Uncapped travel, sample and trade-show reimbursement on top of commission converts a variable cost into a fixed one.",
      },
    ],
    cpgIssues: [
      "Retailer deductions are the reason commission basis matters so much in CPG — a case sold at list can net far less after slotting, promotional allowances, spoilage credits and freight deductions, so commission on gross invoice overpays on every single order.",
      "Free fills and sample product supplied to open a new account are your cost, not the broker's, and should be excluded from the commissionable base.",
      "Brokers often hold the buyer relationship and the retailer paperwork; make sure new-item forms, vendor setup and authorizations are filed in your name so the account stays yours if the broker leaves.",
      "Category review calendars mean the work and the revenue are separated by months — the commission timing clause should reflect that a broker earns on shipped, paid volume rather than on a promise of a future reset.",
      "Promotional and deal volume sold at a discount should be commissioned on the actual net price, or the broker is rewarded most for the volume that makes you the least.",
      "Brokers commonly represent several brands in your category; ask directly whether they carry a competing item and what happens in a category review where both are presented.",
      "Commission rates vary widely by channel, category, region and how much service the broker actually provides — a rate that is reasonable for a broker running resets and deduction follow-up is not the same as one for pure introduction, so compare the rate against the scope of work rather than against a number someone quoted you.",
    ],
    founderMistakes: [
      "Never attaching a house account schedule, then paying commission for years on the two chains the founder personally opened before hiring the broker.",
      "Agreeing to commission on gross invoice, and paying a percentage on slotting and promotional dollars that never reached the bank.",
      "Accepting a perpetual post-termination tail, then continuing to pay a broker who had stopped working the account entirely.",
      "Granting a broker exclusivity across a whole channel with no targets, and being unable to hire a second broker for a region it never worked.",
      "Letting the broker file retailer vendor paperwork in the broker's own name, and losing the authorization when the relationship ended.",
      "Not reversing commission on returns and deductions, so a heavily deducted quarter still carried full commission expense.",
    ],
    negotiationPoints: [
      "Attach a house account schedule by name, and state that house accounts carry no commission unless later agreed in writing.",
      "Base commission on net collected revenue after deductions, allowances, returns and freight, not on gross invoice.",
      "Make commission payable after you are paid by the customer, with a stated payment cycle.",
      "Limit any post-termination tail to accounts the broker actually opened, for a short defined period, and state that it ends there.",
      "Reverse commission automatically on returns, chargebacks and deductions against a commissioned sale.",
      "Tie any exclusivity to account and revenue targets, with automatic conversion to non-exclusive if they are missed.",
      "Cap or pre-approve reimbursable expenses rather than accepting open reimbursement.",
      "Require all retailer vendor paperwork and authorizations to be filed in the brand's name.",
    ],
    redFlags: [
      "No house account definition anywhere in the agreement.",
      "Commission earned on order placement regardless of whether you are ever paid.",
      "A post-termination tail with no end date, or one covering all accounts rather than accounts the broker opened.",
      "Commission calculated on gross invoice with no deduction for allowances or returns.",
      "Exclusive representation with no performance requirement.",
      "Retailer paperwork filed in the broker's name.",
      "No right to terminate on reasonable notice.",
      "A commission rate that applies equally to deep-discount promotional volume and full-price volume.",
    ],
    beforeYouSign: [
      "A written list of every account you already have or are actively working, to be attached as house accounts.",
      "Your net-per-case model showing what a commissioned sale actually leaves you after retailer deductions.",
      "The broker's current brand portfolio and a direct answer on competing items.",
      "The buyer relationships and category review calendars the broker is actually claiming to have.",
    ],
    related: [
      "commission-sales-agreement",
      "retail-broker-agreement",
      "broker-plus-distributor-agreement",
      "broker-distributor-hybrid-agreement",
      "retail-vendor-agreement",
      "commission-agreement",
    ],
    attorneyReview: "yes",
    templateId: "cpg-broker-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "retail-broker-agreement",
    name: "Retail Broker Agreement",
    category: "brokers",
    subcategory: "Channel-specific brokers",
    aliases: [
      "Grocery Broker Agreement",
      "Retail Sales Broker Agreement",
      "Natural Channel Broker Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Retail Broker",
    whenYouNeedIt:
      "When you need representation into grocery, natural, club, convenience or mass retail buyers — particularly a broker with a standing relationship at a chain you cannot get a meeting with.",
    whatItDoes:
      "Engages a broker to present your items to retail buyers, manage the new-item and category review process, and support the account afterwards through promotional planning and resets, in exchange for commission on shipped volume.",
    whyItMatters:
      "In retail the broker is often the only reason a buyer takes your call, but the work and the payoff are separated by an entire category review cycle. That gap is where the money is decided: what counts as a new account the broker opened, what counts as an account you already had, and whether commission survives the broker leaving. Retail also brings the deduction landscape with it, so a commission clause written on gross invoice systematically overpays on every order.",
    majorTerms: [
      "Named retail accounts and banners in scope",
      "Commission rate and net-revenue basis",
      "New account qualification and documentation",
      "House and existing account exclusions",
      "Responsibility for new-item forms and vendor setup",
      "Category review and reset support obligations",
      "Promotional planning and deal submission duties",
      "Handling of retailer chargebacks and deductions",
      "Reporting cadence and account activity updates",
      "Performance targets by account and revenue",
      "Term, termination and commission tail",
    ],
    clausesToWatch: [
      {
        clause: "What counts as opening an account",
        why: "If an introduction counts as an opening, a broker can claim a chain that was already in your pipeline and earn on it indefinitely.",
      },
      {
        clause: "Commission on deducted and promotional volume",
        why: "Retail revenue arrives net of allowances, and commission on the gross number pays the broker out of money you never received.",
      },
      {
        clause: "Ownership of retailer paperwork",
        why: "Vendor numbers, new-item forms and authorizations filed in the broker's name can leave with the broker.",
      },
      {
        clause: "Post-termination tail on chain accounts",
        why: "Chain reorders continue automatically once an item is on a planogram, so an open tail pays for work that has stopped.",
      },
      {
        clause: "Service obligations after the sale",
        why: "Without stated duties for resets, promotional submission and deduction follow-up, the broker is paid for the meeting and not for the account.",
      },
      {
        clause: "Competing brands in the same category review",
        why: "A broker presenting two competing items to the same buyer has a conflict that will not be resolved in your favour by default.",
      },
    ],
    cpgIssues: [
      "Category review calendars set the whole timeline — an item presented out of cycle can wait most of a year, so the agreement should not treat a missed review as broker performance failure or as a sale earned.",
      "New-item forms and retailer portals demand a complete data package including GTINs, nutrition, allergen and certification detail, and the agreement should say who prepares and who owns that submission.",
      "Slotting and free fill demanded by the retailer are your cost, and they should be excluded from the commissionable base rather than treated as revenue.",
      "Deductions arrive months after shipment; if commission has already been paid on gross invoice, you are recovering it from future commission or not at all.",
      "Resets and planogram changes are where distribution is actually lost, so support obligations at reset are worth more than the introduction that got you in.",
      "Broker-supplied demo and sample product needs to be tracked, because it is drawn from your inventory and is easily invisible in the commission math.",
    ],
    founderMistakes: [
      "Paying a full commission on an account the broker introduced but never serviced, which then lost distribution at the next reset.",
      "Letting the broker file the retailer vendor setup under its own name, and having to re-onboard as a new vendor after the relationship ended.",
      "Commissioning on gross invoice and discovering after a heavy promotional quarter that commission expense exceeded net contribution on the account.",
      "Having no written definition of a new account, then paying on a chain the founder had been in conversation with for a year.",
      "Not asking which competing brands the broker carried, and being presented alongside a direct competitor in the same review.",
    ],
    negotiationPoints: [
      "Define an opened account by a written first purchase order, not by an introduction or a meeting.",
      "Commission on net collected revenue, excluding slotting, free fill and promotional allowances.",
      "Require all retailer paperwork, vendor numbers and authorizations to be in the brand's name.",
      "Attach service obligations covering reset participation, promotional submission and deduction follow-up.",
      "Limit any tail to accounts the broker opened, for a defined short period after termination.",
      "Ask for written disclosure of competing items in your categories and a right to terminate if one is added.",
    ],
    redFlags: [
      "An account counted as opened on introduction alone.",
      "Commission on gross shipped dollars with no deduction adjustment.",
      "No service or reset obligations after the initial sale.",
      "Retailer vendor setup filed in the broker's name.",
      "An indefinite post-termination tail on chain accounts.",
      "No disclosure requirement for competing brands.",
    ],
    beforeYouSign: [
      "The broker's actual buyer relationships at the specific banners you care about, and the next review dates.",
      "A written house and existing account schedule.",
      "Your net-per-case model for each target retailer including its deduction structure.",
    ],
    related: [
      "cpg-broker-agreement",
      "retail-vendor-agreement",
      "slotting-placement-agreement",
      "promotional-allowance-agreement",
      "key-account-sales-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "foodservice-broker-agreement",
    name: "Foodservice Broker Agreement",
    category: "brokers",
    subcategory: "Channel-specific brokers",
    aliases: [
      "Foodservice Sales Broker Agreement",
      "Operator Broker Agreement",
      "Away-From-Home Broker Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Foodservice Broker",
    whenYouNeedIt:
      "When you are selling into restaurants, cafes, campuses, healthcare, hospitality or other operators, where the buyer is an operator or a group purchasing organisation and the product ships through a foodservice distributor.",
    whatItDoes:
      "Engages a broker to sell your product to foodservice operators and to get it stocked by the broadline distributors those operators buy from. Commission is paid on volume moving through the distributor to the accounts the broker works.",
    whyItMatters:
      "Foodservice pays on a different mechanic than retail, and founders under-price the difference. The broker sells the operator, but the product ships through a distributor at a deviated price, and the difference between your list price and the operator's contracted price comes back to you as a bill-back. Commission written against list price rather than against the deviated net can turn a foodservice win into a negative-margin account, and the volume can be large enough that the mistake compounds fast.",
    majorTerms: [
      "Operator segments and named accounts in scope",
      "Commission rate and whether it is on net deviated price",
      "Distributor deviation and bill-back mechanics",
      "Which distributors the broker is authorised to work with",
      "Group purchasing organisation account treatment",
      "Sample, cutting and culinary demonstration product supply",
      "Spec-in and menu placement obligations",
      "Reporting on operator volume and distributor movement",
      "House and existing operator account exclusions",
      "Performance targets and account counts",
      "Term, termination and commission tail",
    ],
    clausesToWatch: [
      {
        clause: "Commission basis against deviated price",
        why: "If commission is calculated on list rather than on the net price after deviation and bill-back, you can pay commission on revenue that was never yours.",
      },
      {
        clause: "Bill-back responsibility and validation",
        why: "Without a process to validate distributor bill-back claims, you pay deviation on volume that may not match what the operator actually bought.",
      },
      {
        clause: "Group purchasing organisation accounts",
        why: "A contract negotiated centrally can carry fees and pricing across many operators at once, so treating those as ordinary brokered accounts overstates what the broker earned.",
      },
      {
        clause: "Sample and cutting product",
        why: "Culinary sampling consumes real inventory continuously, and an unlimited supply obligation is a cost line nobody budgets.",
      },
      {
        clause: "Distributor authorisation duties",
        why: "An operator sale is worth nothing if the product is not stocked by the distributor that operator buys from, so listing work must be an obligation, not a favour.",
      },
      {
        clause: "Commission tail on contracted accounts",
        why: "Foodservice contracts run for years, so a tail on a contracted account can outlast the broker relationship several times over.",
      },
    ],
    cpgIssues: [
      "Foodservice pack sizes, case counts and formats usually differ from retail, so confirm the items in scope are actually the foodservice SKUs and not your retail pack.",
      "Deviated pricing and bill-backs are the defining economics of the channel — model your net after deviation before agreeing any commission rate.",
      "Broadline distributor listing requires its own data package and slotting-style fees; agree whether the broker handles the submission and who pays the fees.",
      "Menu spec-in means the operator builds your product into a recipe, which is valuable and sticky, but it also means a formula or pack change can cost you the account.",
      "Culinary cuttings and chef samples draw down real inventory and often require special pack-outs; cap the supply obligation or budget it explicitly.",
      "Shelf life and case dating matter differently in foodservice, where turns can be slower at a single operator location than in retail.",
      "Traceability through a broadline distributor to individual operators is harder than to a retail DC, so lot-code reporting expectations should be set up front.",
    ],
    founderMistakes: [
      "Setting commission against list price and paying it on volume sold at a deep deviation, so a large operator win lost money on every case.",
      "Paying distributor bill-backs without validating them against operator movement reports, and funding deviation on volume that could not be traced.",
      "Winning an operator without getting the product listed at that operator's distributor, so the account never generated an order.",
      "Supplying unlimited culinary samples with no cap, and consuming a meaningful share of a production run on cuttings.",
      "Treating a group purchasing organisation win as a single brokered account when it carried administrative fees across dozens of operators.",
    ],
    negotiationPoints: [
      "Base commission on net revenue after deviation, bill-backs and any distributor allowances.",
      "Require operator movement reporting to validate bill-back claims before they are paid.",
      "Make distributor listing and authorisation an express broker obligation with a timeline.",
      "Cap sample and cutting product per period, or require pre-approval above a set quantity.",
      "Treat group purchasing organisation and contracted accounts under a separate stated commission arrangement.",
      "Limit the commission tail on multi-year contracted accounts to a short defined period.",
    ],
    redFlags: [
      "Commission on list price with no adjustment for deviation.",
      "No validation process for distributor bill-back claims.",
      "No obligation to obtain distributor listings for the operators being sold.",
      "Unlimited sample and cutting product supply.",
      "A tail that runs for the length of a multi-year operator contract.",
      "No distinction between street accounts and contracted or group purchasing accounts.",
    ],
    beforeYouSign: [
      "Your net-per-case model after deviation, bill-back and distributor allowances for the target operator segments.",
      "A list of the broadline distributors you are already listed with, and the gaps.",
      "The broker's actual operator relationships and segment focus, in writing.",
    ],
    related: [
      "cpg-broker-agreement",
      "food-distributor-agreement",
      "commission-sales-agreement",
      "key-account-sales-agreement",
      "wholesale-distribution-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "independent-sales-rep-agreement",
    name: "Independent Sales Representative Agreement",
    category: "brokers",
    subcategory: "Individual representatives",
    aliases: [
      "Manufacturers Rep Agreement",
      "Independent Rep Agreement",
      "Sales Agent Agreement",
      "1099 Sales Rep Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Independent Sales Representative",
    whenYouNeedIt:
      "When you engage an individual, rather than a brokerage firm, to sell your product on commission — a regional rep working several non-competing lines, or a former buyer selling into their old accounts.",
    whatItDoes:
      "Sets the terms on which an individual represents your brand on commission: what they may sell, where, at what rate, when they get paid, what they may not do, and how the relationship ends. It also defines the relationship as an independent contractor rather than employment.",
    whyItMatters:
      "Two risks sit in this document that are not in a firm-level broker agreement. The first is classification: a rep you direct, schedule, equip and control day to day looks less like an independent contractor as time goes on, and misclassification exposure runs to back taxes, benefits and penalties rather than just a bad deal. The second is that individual reps often carry your accounts personally, so how commission and account ownership are written determines whether those accounts stay with you when the rep leaves.",
    majorTerms: [
      "Commission rate and net-revenue basis",
      "When commission is earned and the payment cycle",
      "Commission on repeat and reorder volume",
      "Draw against commission, if any, and repayment terms",
      "Territory, accounts and lines in scope",
      "House and existing account exclusions",
      "Independent contractor status and control limits",
      "Expense responsibility and reimbursement",
      "Non-competing lines the rep may carry",
      "Confidentiality and customer list ownership",
      "Term, termination and notice",
      "Post-termination commission tail",
    ],
    clausesToWatch: [
      {
        clause: "Independent contractor status and day-to-day control",
        why: "Terms that set hours, require exclusivity of effort and impose detailed supervision undercut the contractor characterisation and create exposure a commission saving will not cover.",
      },
      {
        clause: "Draw against commission",
        why: "An unrecoverable draw is compensation; a recoverable one can leave a departing rep owing money, and the repayment terms need to be explicit before anyone relies on it.",
      },
      {
        clause: "Commission payment timing",
        why: "Rules about how quickly commissions must be paid to sales representatives vary by state, so the payment cycle you write is a question worth putting to counsel rather than assuming.",
      },
      {
        clause: "Customer list and account ownership",
        why: "Without a clear statement that accounts and customer data belong to the brand, a rep can treat your account list as personal and take it to the next line.",
      },
      {
        clause: "Competing lines",
        why: "Reps carry portfolios; if competing is not defined at the category and sub-category level you may find your item sitting next to a near-identical one in the same bag.",
      },
      {
        clause: "Post-termination tail",
        why: "An open tail on reorders means you pay a departed rep on accounts your own team now services.",
      },
    ],
    cpgIssues: [
      "Reps often carry sample inventory, coolers and point-of-sale material belonging to the brand; keep a schedule and require return on termination or it simply disappears.",
      "Retailer and distributor paperwork filed by a rep should be in the brand's name, since the vendor relationship is what you are actually paying to build.",
      "Sample product drawn for buyer meetings, cuttings and demos is real cost of goods; set a periodic allowance rather than an open cabinet.",
      "Deductions and chargebacks arrive after the sale, so commission has to be reversible or your rep is paid on revenue that later evaporates.",
      "Reps working several non-competing food lines can be enormously effective, but you need a written definition of competing that covers adjacent sub-categories, not just identical products.",
      "Demo and merchandising work performed by a rep in-store can look like employment activity, which is another reason the control terms matter.",
    ],
    founderMistakes: [
      "Treating a full-time-equivalent rep as a contractor while setting their schedule and requiring exclusive effort, and facing a classification problem later.",
      "Paying an advance described loosely as a draw with no written recovery terms, then having no way to recover it when the rep left.",
      "Never defining competing lines, and discovering the rep had added a near-identical product to the same portfolio.",
      "Letting a rep hold the buyer relationship and the account records personally, then losing the accounts when they moved to another brand.",
      "Failing to reverse commission on deducted and returned volume, so commission expense outran net revenue on a promotional quarter.",
    ],
    negotiationPoints: [
      "Keep the relationship genuinely independent: define deliverables and outcomes rather than hours, methods and supervision.",
      "State the commission basis as net collected revenue and make commission reversible on returns and deductions.",
      "Put any draw in writing, state whether it is recoverable, and say what happens to a negative balance on termination.",
      "Confirm in writing that accounts, customer lists and retailer paperwork belong to the brand.",
      "Define competing lines at the sub-category level and require disclosure of every line carried.",
      "Limit any post-termination tail to accounts the rep opened, for a short stated period.",
      "Attach a schedule of brand property held by the rep, with a return obligation.",
    ],
    redFlags: [
      "Contractor language sitting alongside terms that dictate hours, territory presence and daily supervision.",
      "A draw with no written recovery terms.",
      "No definition of competing lines, or a definition limited to identical products.",
      "Customer lists or account relationships treated as the rep's own.",
      "An indefinite commission tail.",
      "No return obligation for samples, coolers, displays or point-of-sale material.",
    ],
    beforeYouSign: [
      "A written list of every other line the rep carries.",
      "Your house and existing account schedule.",
      "A decision, with counsel, on whether this role is genuinely a contractor engagement or an employment one.",
      "A schedule of brand property the rep will hold.",
    ],
    related: [
      "commission-sales-agreement",
      "cpg-broker-agreement",
      "independent-contractor-agreement",
      "commission-agreement",
      "sales-employee-compensation-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Worker classification and the rules governing when sales commissions must be paid vary by jurisdiction, and getting either wrong creates exposure well beyond the commission itself.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "commission-sales-agreement",
    name: "Commission Sales Agreement",
    category: "brokers",
    subcategory: "Commission mechanics",
    aliases: [
      "Sales Commission Agreement",
      "Commission Structure Agreement",
      "Commission Plan",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Broker, Rep or Sales Partner",
    whenYouNeedIt:
      "Whenever someone is paid a percentage of sales rather than a fee — a broker, an outside rep, a partner introducing accounts. Use it as a standalone document or as the commission schedule attached to a broader agreement.",
    whatItDoes:
      "Defines the entire commission mechanic in one place: the rate, the revenue base it applies to, the event that earns it, the event that pays it, what reduces or reverses it, and what happens to it after the relationship ends.",
    whyItMatters:
      "Almost every commission dispute traces back to four terms nobody wrote down: the base the percentage applies to, the moment commission is earned, whether reorders keep paying, and how long payments continue after termination. In CPG the base question is the most expensive, because gross shipped dollars and net collected dollars can be very far apart once slotting, promotional allowances, spoilage credits and freight deductions land. Writing commission against the wrong number can make your best-selling account your least profitable one.",
    majorTerms: [
      "Commission rate and any tiering",
      "The revenue base: gross invoice or net collected",
      "Exclusions from the base: slotting, allowances, freight, samples, taxes",
      "Earning event: order, shipment, invoice or customer payment",
      "Payment cycle and timing",
      "Commission on repeat and reorder volume",
      "Reversal on returns, chargebacks and deductions",
      "Split commissions where more than one party is involved",
      "House and existing account exclusions",
      "Draw, advance or guarantee terms",
      "Post-termination commission tail and its end date",
      "Statement, audit and dispute rights",
    ],
    clausesToWatch: [
      {
        clause: "Definition of the commissionable base",
        why: "Gross invoice includes money you will never collect in a channel built on deductions, so the base definition is the single largest number in the agreement.",
      },
      {
        clause: "Earning event",
        why: "Commission earned on order rather than on payment shifts the customer's credit risk onto you while the other side is already paid.",
      },
      {
        clause: "Reorder and repeat-order commission",
        why: "Silence usually gets read as continuing forever, which means you keep paying on automatic reorders nobody is working.",
      },
      {
        clause: "Reversal and clawback",
        why: "Without automatic reversal, a returned or deducted sale leaves commission paid on revenue that no longer exists and no mechanism to recover it.",
      },
      {
        clause: "Post-termination tail",
        why: "A tail with no end date is an annuity written against your margin for accounts that are no longer being serviced.",
      },
      {
        clause: "Split commissions",
        why: "When a broker, a rep and a distributor all touch the same account, an undefined split means you can be asked to pay more than once on the same case.",
      },
      {
        clause: "Statement and audit rights",
        why: "If the other side calculates its own commission with no statement obligation, errors run in one direction and are found late.",
      },
    ],
    cpgIssues: [
      "Slotting fees, free fills and promotional allowances should be excluded from the base by name, because they are cash out of your pocket rather than revenue in.",
      "Spoilage, unsaleable and out-of-date credits arrive months after shipment and must reverse commission, or a short-shelf-life product will carry commission on product that was destroyed.",
      "Freight, fuel surcharges and pallet fees deducted by a retailer should sit outside the commissionable base.",
      "Deep-discount promotional volume should carry a different rate or be commissioned on actual net, or you pay the most on the volume that earns the least.",
      "Deduction timing in CPG runs long, so the reversal window has to be long enough to catch chargebacks that land two quarters after the sale.",
      "Sample, demo and free-fill cases drawn from inventory are cost of goods, not commissionable revenue, and should be named as excluded.",
    ],
    founderMistakes: [
      "Writing commission on gross shipped dollars in a channel where deductions take a large and variable share of the invoice, and paying commission on money never received.",
      "Not defining the earning event, then paying commission on a large opening order that the retailer later cancelled and returned.",
      "Leaving reorder commission unaddressed, and paying indefinitely on an account that reorders automatically through a portal.",
      "Agreeing to a post-termination tail with no end date because the relationship felt permanent.",
      "Having no statement or audit right, and finding a rate error only after two years of overpayment.",
    ],
    negotiationPoints: [
      "Define the base as net revenue actually collected, and list the exclusions by name.",
      "Set the earning event at customer payment, with a stated payment cycle after collection.",
      "Provide for automatic reversal of commission on returns, chargebacks, deductions and credits, with a reversal window that reflects real deduction timing.",
      "Set a separate, lower rate for deep-discount promotional volume, or commission it on actual net.",
      "Put an end date on any post-termination tail and limit it to accounts the other side opened.",
      "Require a written commission statement each cycle and a right to audit the calculation.",
      "Address splits expressly where a broker, a rep and a distributor can all touch one account.",
    ],
    redFlags: [
      "Commission on gross invoice with no exclusions.",
      "Commission earned on order placement.",
      "No reversal mechanism for returns or deductions.",
      "A perpetual or undefined post-termination tail.",
      "No commission statement obligation.",
      "The same rate applied to full-price and deep-promotional volume.",
      "The counterparty calculating and deducting its own commission from your collections.",
    ],
    beforeYouSign: [
      "A worked example of the commission calculation on a real recent order, from gross invoice down to net collected.",
      "A written list of everything excluded from the commissionable base.",
      "Your model of total commission expense at the volume you expect, against contribution margin.",
    ],
    related: [
      "cpg-broker-agreement",
      "independent-sales-rep-agreement",
      "commission-agreement",
      "key-account-sales-agreement",
      "broker-distributor-hybrid-agreement",
    ],
    attorneyReview: "yes",
    templateId: "commission-sales-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "key-account-sales-agreement",
    name: "Key Account Sales Agreement",
    category: "brokers",
    subcategory: "Individual representatives",
    aliases: [
      "National Account Sales Agreement",
      "Key Account Management Agreement",
      "Strategic Account Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Key Account Manager or Agency",
    whenYouNeedIt:
      "When you engage someone specifically to win and manage a small number of named large accounts, usually with a retainer alongside commission, rather than general territory representation.",
    whatItDoes:
      "Assigns responsibility for a defined list of named accounts to one party, sets what they must do on each account, and pays them through some combination of retainer and commission tied to that specific account list.",
    whyItMatters:
      "Key account work is expensive because it is paid partly in retainer, and the retainer keeps running whether or not the account is won. The contract has to say what the retainer buys — meetings held, submissions made, reviews attended — and what happens when an account is lost or never opened. It also has to say what happens to a named account after termination, because these are exactly the accounts that continue reordering on their own and that a departing party will claim a tail on.",
    majorTerms: [
      "Named account list and how it is amended",
      "Retainer amount, term and what it covers",
      "Commission rate on named account volume",
      "Whether retainer is creditable against commission",
      "Deliverables: submissions, reviews, joint business planning",
      "Milestones for winning each named account",
      "Reporting and account status updates",
      "Treatment of accounts that are lost or never opened",
      "Exclusivity over the named account list",
      "Term, termination and notice",
      "Post-termination treatment of named accounts",
    ],
    clausesToWatch: [
      {
        clause: "What the retainer buys",
        why: "A retainer with no stated deliverables becomes a monthly payment for availability rather than for work on your accounts.",
      },
      {
        clause: "Retainer creditable against commission",
        why: "If it is not creditable you are paying twice for the same account, which is only worth it if the retainer buys work you could not otherwise get.",
      },
      {
        clause: "Named account exclusivity",
        why: "Locking your largest prospects to one party means you cannot pursue them yourself or through anyone else while the agreement runs.",
      },
      {
        clause: "Accounts never opened",
        why: "Without a milestone and a release, a named account can sit assigned and unworked for the whole term while you pay the retainer.",
      },
      {
        clause: "Post-termination treatment of named accounts",
        why: "These accounts reorder without effort once listed, so a tail here is larger and lasts longer than on ordinary brokered volume.",
      },
      {
        clause: "Conflicts on the same account",
        why: "A key account manager working several brands into the same buyer has divided loyalty in exactly the meeting you are paying for.",
      },
    ],
    cpgIssues: [
      "Large accounts bring their own deduction structure, so commission on named account volume should be written on net collected revenue after that retailer's specific allowances.",
      "Joint business planning with a large retailer commits promotional spend far in advance; make sure the agreement says who has authority to commit your budget.",
      "Named account wins usually require slotting or new-item investment from you, so the payment structure should recognise that the win itself carries a cash cost.",
      "Vendor portals, EDI setup, routing guide compliance and chargeback management are ongoing obligations on a large account; state whether the key account manager handles them.",
      "Supply readiness matters as much as the sale — winning a national account you cannot produce for is a worse outcome than not winning it, so production capacity should be a stated milestone.",
      "Category review timing means a named account can take a year or more to open, which should shape the milestone schedule rather than the termination clause.",
    ],
    founderMistakes: [
      "Paying a retainer for a year against a named account list where no submission was ever made, because the agreement listed no deliverables.",
      "Letting a key account manager commit promotional spend in a joint business plan the founder had never approved.",
      "Winning a large account without the production capacity to service it, and losing the listing on fill-rate failures.",
      "Locking their biggest prospects into an exclusive named account list, then being unable to pursue them directly when the relationship stalled.",
      "Accepting a long tail on named accounts that reorder automatically, paying for years on business that ran itself.",
    ],
    negotiationPoints: [
      "Attach deliverables and milestones to the retainer, with a release of any account not moved forward by a stated date.",
      "Make the retainer creditable against commission earned on the same accounts.",
      "Require written approval before any promotional spend or trade commitment is made on your behalf.",
      "Commission on net collected revenue after that retailer's deductions.",
      "Limit exclusivity to accounts actively being worked, with accounts released back to you if milestones are missed.",
      "Set a short, defined post-termination tail and state that it ends there.",
    ],
    redFlags: [
      "A retainer with no deliverables, milestones or release mechanism.",
      "Authority to commit trade spend without written approval.",
      "Exclusivity over a named account list with no activity requirement.",
      "Commission on gross shipments to a large deducting retailer.",
      "A long or open-ended tail on named accounts.",
      "No disclosure of other brands being taken into the same buyer.",
    ],
    beforeYouSign: [
      "The named account list, with a milestone and a target date against each one.",
      "A written statement of what the retainer covers each month.",
      "Confirmation of your production capacity and fill-rate readiness for the accounts on the list.",
      "The deduction structure of each target retailer, modelled into your net.",
    ],
    related: [
      "cpg-broker-agreement",
      "retail-broker-agreement",
      "commission-sales-agreement",
      "retail-vendor-agreement",
      "promotional-allowance-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "broker-plus-distributor-agreement",
    name: "Broker + Distributor Agreement",
    category: "brokers",
    subcategory: "Combined arrangements",
    aliases: [
      "Broker and Distributor Structure",
      "Two-Tier Sales Agreement",
      "Broker Overlay Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Broker + Distributor (separate agreements, coordinated terms)",
    whenYouNeedIt:
      "When you have both a broker representing you to the retailer and a distributor shipping the product, and you are paying both on the same case. This is the standard structure in much of retail grocery and it needs to be modelled before you agree to either half.",
    whatItDoes:
      "Coordinates the two agreements so that the broker's commission and the distributor's margin are defined against different, non-overlapping things, and so that responsibility for retailer paperwork, promotional funding, deductions and account ownership is assigned to one party rather than assumed by both.",
    whyItMatters:
      "Founders price the broker and the distributor separately and only discover the combined cost when the first payment cycle closes. A broker commission on gross shipments stacked on top of a distributor margin, plus promotional funding, slotting and deductions, can leave a case that looked profitable at list price contributing nothing. The second problem is duplication: without coordination the broker and the distributor can both claim credit for the same account, both submit promotional plans, and both bill you for work on the same sale.",
    majorTerms: [
      "Broker commission rate and the base it applies to",
      "Distributor margin and price to distributor",
      "Whether broker commission is calculated before or after distributor margin",
      "Division of duties: who calls the buyer, who ships, who merchandises",
      "Which party owns the retailer relationship and paperwork",
      "Who submits and funds promotional plans",
      "Deduction and chargeback responsibility between the two",
      "House accounts excluded from both",
      "Non-duplication of compensation on the same sale",
      "Reporting from each party and how it reconciles",
      "Termination of one agreement and its effect on the other",
    ],
    clausesToWatch: [
      {
        clause: "Commission base relative to distributor margin",
        why: "Broker commission calculated on retail-equivalent or gross shipment value rather than on your actual net after distributor margin can multiply the real cost several times over.",
      },
      {
        clause: "Non-duplication of compensation",
        why: "Without an express provision, both parties can be paid for the same account activity and you have no basis to refuse either invoice.",
      },
      {
        clause: "Promotional funding responsibility",
        why: "If both parties can submit deals against your budget, you can fund the same promotion twice and discover it only in the deduction report.",
      },
      {
        clause: "Retailer relationship ownership",
        why: "When both a broker and a distributor claim the account, losing either one can cost you the listing because neither has an obligation to hand it over.",
      },
      {
        clause: "Deduction responsibility",
        why: "Retailer deductions can be absorbed in distributor margin, passed to you, or netted from broker commission, and unless one is chosen you will be arguing about each one.",
      },
      {
        clause: "Interdependence of the two agreements",
        why: "If terminating the distributor leaves a broker agreement running with nothing to sell through, you keep paying for representation that cannot produce an order.",
      },
    ],
    cpgIssues: [
      "The stacked cost of market access is the whole point of this structure: model your net after distributor margin, broker commission, slotting, promotional allowances, spoils and freight before agreeing either rate.",
      "Retailer authorizations and vendor numbers should sit in the brand's name so that losing the broker or the distributor does not cost you the shelf.",
      "Promotional calendars submitted through a distributor and through a broker can double-book the same deal period, generating deductions from both directions.",
      "Sell-through reporting arrives in different formats from a broker and a distributor, so agree a reconciliation basis or you will never trust either number.",
      "Free fills and samples requested by the broker are drawn from inventory the distributor bought, so it has to be clear whose cost they are.",
      "Chargebacks and unsaleable credits at the retailer flow through the distributor first, which means the broker's commission reversal has to be tied to distributor-reported net.",
    ],
    founderMistakes: [
      "Agreeing a broker rate and a distributor margin in separate conversations, and finding the combined cost of market access exceeded gross margin on the item.",
      "Calculating broker commission on gross shipment value rather than on brand net revenue, effectively paying commission on the distributor's margin too.",
      "Letting both the broker and the distributor submit promotional plans, and funding the same deal period twice.",
      "Having no non-duplication clause, and paying commission on accounts the distributor had opened independently.",
      "Terminating a distributor and leaving a broker agreement in force with no route to market, still accruing minimum commission.",
    ],
    negotiationPoints: [
      "Define broker commission on brand net revenue after distributor margin and deductions, and say so explicitly in both documents.",
      "Include a non-duplication provision so no single sale generates both a commission and a second fee for the same work.",
      "Assign promotional plan submission to one party and require your written approval before any deal is committed.",
      "Put retailer vendor setup and authorizations in the brand's name in both agreements.",
      "Align termination and notice periods so ending one does not leave the other stranded.",
      "Agree a single reconciliation basis for reporting from both parties.",
    ],
    redFlags: [
      "Broker commission calculated on gross or retail-equivalent value rather than brand net.",
      "No non-duplication clause across the two agreements.",
      "Both parties able to commit your promotional budget.",
      "Retailer paperwork held by either party rather than the brand.",
      "Mismatched terms so one agreement cannot be exited without breaching the other.",
      "No agreed source of truth for sell-through reporting.",
    ],
    beforeYouSign: [
      "A single combined model showing brand net per case after distributor margin, broker commission and all retailer deductions.",
      "A written division of duties between the broker and the distributor.",
      "Confirmation that retailer paperwork will be filed in the brand's name.",
    ],
    related: [
      "broker-distributor-hybrid-agreement",
      "cpg-broker-agreement",
      "food-distributor-agreement",
      "commission-sales-agreement",
      "retail-vendor-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "international-sales-rep-agreement",
    name: "International Sales Representative Agreement",
    category: "brokers",
    subcategory: "Cross-border representation",
    aliases: [
      "Overseas Sales Agent Agreement",
      "Foreign Sales Representative Agreement",
      "Export Sales Rep Agreement",
    ],
    stages: ["growth", "distribution"],
    importance: "advanced",
    entityTypes: ["any"],
    parties: "Brand + Sales Representative outside the home country",
    whenYouNeedIt:
      "When someone in another country will represent your brand on commission — introducing you to importers, distributors or retailers there — without buying the product themselves.",
    whatItDoes:
      "Appoints a commission representative in a foreign market, defines the accounts and territory, sets the commission mechanic in a stated currency, and fixes which country's law and forum govern the relationship.",
    whyItMatters:
      "Commission representation across borders carries risks that a domestic broker agreement does not. A number of countries have their own rules on how sales agents and representatives may be terminated and what compensation may be owed on termination, and those rules vary substantially by country — this needs local counsel in the specific market before you sign, not after you give notice. There is also a practical enforcement problem: a commission dispute with someone whose assets are all in another country is hard to pursue, so the payment mechanics matter more than the remedies.",
    majorTerms: [
      "Country or region and accounts in scope",
      "Commission rate, currency and payment mechanism",
      "Whether the appointment is exclusive and on what conditions",
      "Performance requirements in local terms",
      "Confirmation that the representative may not take title or bind the brand",
      "Anti-corruption and compliance undertakings",
      "Confidentiality and customer information ownership",
      "Term, notice and termination mechanics",
      "Post-termination commission treatment",
      "Governing law, forum and dispute resolution",
    ],
    clausesToWatch: [
      {
        clause: "Termination notice and any exit compensation",
        why: "Local rules on ending agent and representative relationships differ by country and can attach consequences to termination, so this belongs with local counsel before signature.",
      },
      {
        clause: "Authority to bind the brand",
        why: "A representative who appears able to accept orders, agree prices or make commitments on your behalf can create obligations you never approved.",
      },
      {
        clause: "Currency and payment mechanics",
        why: "Commission owed in one currency on revenue collected in another creates a moving cost, and cross-border payment restrictions can complicate paying it at all.",
      },
      {
        clause: "Anti-corruption undertakings",
        why: "Payments made by a representative on your behalf in a foreign market can create exposure for the brand, so the compliance obligations and audit rights need to be real.",
      },
      {
        clause: "Governing law and forum",
        why: "A clause pointing to a forum where you could not practically enforce an outcome leaves you with a remedy on paper only.",
      },
      {
        clause: "Exclusive appointment over a whole country",
        why: "Combined with uncertain termination rules, an exclusive national appointment can lock a market you cannot easily reopen.",
      },
    ],
    cpgIssues: [
      "A representative who introduces you to an importer or distributor should not also end up holding the local trademark registration or the product registrations — keep both in your name.",
      "Local labelling, language and permitted-claim requirements differ, so a representative promising rapid launch may be describing a timeline that ignores compliance work.",
      "Samples shipped for buyer meetings cross customs and may need documentation and duty payment; agree who arranges and who pays.",
      "Shelf life is consumed in transit on export shipments, so any performance expectation should account for real lead times rather than domestic ones.",
      "Certificates of analysis, free-sale and health certificates are commonly needed for the accounts a representative opens; agree who obtains them.",
      "Commission on export orders should be calculated on your net after freight, duty and any local compliance costs, which can be a very different number from your domestic net.",
    ],
    founderMistakes: [
      "Granting exclusive national representation on an inbound approach with no performance minimums, then finding the market closed and hard to reopen.",
      "Not taking local advice on termination before signing, and treating notice as a simple contractual question when it was not.",
      "Allowing a representative to appear to buyers as though it could agree prices and terms, and being held to commitments never approved.",
      "Letting the representative register the trademark or hold product registrations locally as a convenience.",
      "Setting commission on gross export invoice without deducting freight, duty and compliance cost, and losing money on every shipment.",
    ],
    negotiationPoints: [
      "Take local counsel in the destination country on termination and any agent compensation rules before signing.",
      "State clearly that the representative has no authority to bind the brand, accept orders or agree pricing.",
      "Set performance minimums in local terms with a stated remedy, and prefer non-exclusive or a short initial exclusive term.",
      "Fix the commission currency, the conversion basis and the payment route, and base commission on net after freight, duty and compliance cost.",
      "Include anti-corruption undertakings, a right to audit and a right to terminate for breach of them.",
      "Require that the brand owns all customer information and any local registrations or filings.",
    ],
    redFlags: [
      "Exclusive representation for a whole country with no minimums and no defined termination path.",
      "Any apparent authority for the representative to accept orders or set prices.",
      "The representative holding, or free to file, local trademark or product registrations.",
      "No anti-corruption undertaking or audit right.",
      "Governing law and forum you could not practically use.",
      "Commission on gross export invoice with no deduction for freight, duty or compliance costs.",
    ],
    beforeYouSign: [
      "Local counsel in the destination country on termination and agent protection rules.",
      "A trademark filing plan for the market in your own name.",
      "Your net-per-case model on export shipments after freight, duty and compliance costs.",
      "The representative's existing brand portfolio and references in the market.",
    ],
    related: [
      "international-sales-agent-agreement",
      "international-distributor-agreement",
      "export-agent-agreement",
      "cpg-broker-agreement",
      "commission-sales-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Rules on terminating sales agents and representatives, and any compensation owed on termination, differ by country and are not something to discover after notice has been given.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default BROKERS_DOCS;
